import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useEmployeeDocumentsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

// mui-datatables@4.3.0 `options.filterList`'i okumaz (bkz.
// reference_datatable_filterlist_ignored) — bu yüzden açılıştaki `missingOnly`
// varsayılanı bir DataTable çipiyle GÖSTERİLEMEZ. Bunun yerine kendi şeridimiz var:
// metin + "Tümünü Göster". Şerit gerçekten uygulanan sorgudan türetilir (bkz.
// `showingMissingOnly`), sabit bir metin DEĞİL.
const FilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(2, 145, 239, 0.07);
  border: 1.5px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 10px 14px;
  margin-bottom: 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FilterBannerClear = styled.button`
  font-family: inherit;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 0;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.main};
  padding: 4px 2px;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
`;

/**
 * Süregelen istihdam belgeleri (spec §11 `document.missing` kuralının EM tarafındaki girişi).
 *
 * Aksiyonlar durum makinesini izler (bkz. EmployeeDocumentRequirement model yorumu):
 *   pending|rejected → Yükle            uploaded → Karar Ver (onayla/reddet)
 *   approved/waived DIŞINDAKİLER → Feragat        iptal her durumda
 * Kapıların TEK doğrulama kaynağı backend'dir; buradaki filtreleme yalnız kullanıcıya
 * anlamsız seçenek göstermemek için.
 */
export default function EmployeeDocumentsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('EM') : false;

  // Bu ekran artık arşiv değil BEKLEYEN İŞLER görünümü: kişi-bazlı tam dosya
  // /personnel-files'ta. Varsayılan filtre eksik + süresi geçmiş belgeler.
  // `missingOnlyCleared` DataTable'ın sayfalama/sıralama state'inden AYRI tutulur —
  // aksi halde her `onTableChanged` çağrısı `parseTableParams`'ın ürettiği TAZE
  // params objesiyle `missingOnly`'yi sessizce düşürür (sayfa değiştirince
  // varsayılan filtrenin kaybolması, kullanıcının "kayıtlar kayboldu" sanmasına
  // yol açardı — Görev 12 brifinginin asıl uyardığı hata budur).
  const [tableParams, setTableParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [missingOnlyCleared, setMissingOnlyCleared] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploading, setUploading] = useState(null); // { id, title }
  const [deciding, setDeciding] = useState(null);   // { id, title }
  const [viewing, setViewing] = useState(null);     // { fileUrl, title }

  const showingMissingOnly = !missingOnlyCleared;
  const queryParams = useMemo(
    () => (showingMissingOnly ? { ...tableParams, missingOnly: true } : tableParams),
    [tableParams, showingMissingOnly],
  );

  const {
    fetching, data, pagination, columns: baseColumns,
    createSchema, uploadSchema, decideSchema,
    create, upload, decide, waive, cancel,
  } = useEmployeeDocumentsApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    setTableParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleClearMissingOnly = useCallback(() => setMissingOnlyCleared(true), []);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleCreate = useCallback(async (formData) => {
    const ok = await create(formData);
    if (ok) setCreateOpen(false);
    return ok;
  }, [create]);

  const handleUpload = useCallback(async (formData) => {
    const ok = await upload(uploading.id, formData);
    if (ok) setUploading(null);
    return ok;
  }, [upload, uploading]);

  const handleDecide = useCallback(async (formData) => {
    const ok = await decide(deciding.id, formData);
    if (ok) setDeciding(null);
    return ok;
  }, [decide, deciding]);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];

    // Müşteri şikayeti: "dosya açılamıyor". Ham fileUrl ASLA açılmaz — erişim kararını
    // FileAccessGuard verir, imzalı adres DocumentViewerModal üzerinden (kendi
    // /v1/storage/view çağrısıyla) alınır; belge artık yeni sekmede değil uygulama-içi
    // modalda açılır (ürün sahibi kararı).
    // AKSİYON TİPLERİ ANLAMLI OLMALI (müşteri bildirimi 2026-08-18): burada beş aksiyonun
    // dördü `edit` tipindeydi, yani hepsi aynı turuncu kalem ikonunu gösteriyordu — "Yükle"
    // ile "Feragat Et" görsel olarak ayırt edilemiyordu. Tip aynı zamanda OptionsMenu'de
    // React key'i olduğu için mükerrer key de üretiyordu (bkz. OptionsMenu.js:iconMap).
    if (row.fileUrl) {
      actions.push({
        type: 'details',
        label: t('personnelFile.openFile'),
        onClick: () => setViewing({ fileUrl: row.fileUrl, title: row.title }),
      });
    }

    if (row.status === 'pending' || row.status === 'rejected') {
      actions.push({
        type: 'upload',
        label: t('employeeDocument.action.upload'),
        onClick: () => setUploading({ id: row.id, title: row.title }),
      });
    }
    if (row.status === 'uploaded') {
      actions.push({
        type: 'decide',
        label: t('employeeDocument.action.decide'),
        onClick: () => setDeciding({ id: row.id, title: row.title }),
      });
    }
    if (row.status !== 'approved' && row.status !== 'waived' && row.status !== 'cancelled') {
      actions.push({
        type: 'waive',
        label: t('employeeDocument.action.waive'),
        onClick: () => setConfirm({
          message: t('employeeDocument.confirm.waive', { title: row.title }),
          onYes: () => waive(row.id),
        }),
      });
    }
    if (row.status !== 'cancelled') {
      actions.push({
        // İptal bir SİLME değil, durum geçişi — kırmızı çöp kutusu yanlış vaatti.
        type: 'cancel',
        label: t('employeeDocument.action.cancel'),
        onClick: () => setConfirm({
          message: t('employeeDocument.confirm.cancel', { title: row.title }),
          onYes: () => cancel(row.id),
        }),
      });
    }
    return actions;
  }, [t, writable, waive, cancel]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          // serverSide (DataTable varsayılanı) → istemci kopyayı yeniden sıralamaz, bu indeksleme
          // güvenlidir. Bkz. ProbationReviewsPage'teki aynı not.
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('employeeDocument.new')}
        schema={createSchema}
        initialValues={null}
        onSave={handleCreate}
      />

      <GenericFormDrawer
        open={!!uploading}
        onClose={() => setUploading(null)}
        title={t('employeeDocument.uploadTitle', { title: uploading?.title || '' })}
        schema={uploadSchema}
        initialValues={null}
        onSave={handleUpload}
      />

      <GenericFormDrawer
        open={!!deciding}
        onClose={() => setDeciding(null)}
        title={t('employeeDocument.decideTitle', { title: deciding?.title || '' })}
        schema={decideSchema}
        initialValues={null}
        onSave={handleDecide}
      />

      {showingMissingOnly && (
        <FilterBanner>
          <span>{t('employeeDocument.filterBanner')}</span>
          <FilterBannerClear type="button" onClick={handleClearMissingOnly}>
            {t('employeeDocument.filterBannerClear')}
          </FilterBannerClear>
        </FilterBanner>
      )}

      <DataTable
        title={t('employeeDocument.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={writable}
        onNewButtonClick={() => setCreateOpen(true)}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />

      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Container>
  );
}
